import clsx from "clsx";
import React, { DetailedHTMLProps, forwardRef, ReactNode, TextareaHTMLAttributes } from "react";
import { useFormContext } from "react-hook-form";

import { FormField } from "../form-field/form-field";
import { FormFieldContainer } from "../form-field/form-field-container";
import { FormFieldLabel } from "../form-field/form-field-label";
import { FormFieldMessage } from "../form-field/form-field-message";
import {
  FormFieldColors,
  FormFieldLabelProps,
  FormFieldMessageProps,
  FormFieldStatus,
} from "../form-field/types";

export interface TextAreaProps
  extends DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
    FormFieldLabelProps,
    FormFieldMessageProps {
  color?: FormFieldColors;
  optionalLabel?: string;
  collapseMessage?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      color = FormFieldColors.Light,
      message,
      status = FormFieldStatus.Info,
      required,
      placeholder,
      label,
      labelDecoration,
      info,
      className,
      optionalLabel,
      onKeyDown,
      collapseMessage = true,
      ...rest
    },
    ref
  ) => {
    const { disabled } = rest;

    const formMethods = useFormContext();
    const hasValue = !!rest.value || (rest?.name && formMethods?.watch(rest.name)) || false;

    return (
      <FormFieldContainer disabled={disabled} className={clsx(className, "flex flex-col")}>
        <FormFieldLabel
          required={required}
          label={label}
          info={info}
          labelDecoration={labelDecoration}
          optionalLabel={optionalLabel}
        />
        <FormField color={color} className="flex-1" hasValue={hasValue}>
          <textarea
            ref={ref}
            required={required}
            {...rest}
            disabled={disabled}
            placeholder={placeholder}
            className="px-2 py-1 h-full resize-none whitespace-pre-wrap"
            onKeyDown={
              onKeyDown ??
              // When pressing Enter, event propagation can prevent a newline from
              // being inserted. In general, we want to avoid this behaviour.
              ((e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                }
              })
            }
          />
        </FormField>
        {(message || !collapseMessage) && <FormFieldMessage message={message} status={status} />}
      </FormFieldContainer>
    );
  }
);
